import { createContext, useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { EvidenceQuery } from "../evidence.query";
import { EvidenceAPI } from "../evidence.api";
import { Evidence } from "../evidence.types";
import { useEvidenceColumnDefinitions } from "../Hooks";

export type EvidenceContextType = {
  data: Evidence | undefined;
  isLoading: boolean;
  isFetched: boolean;
  refetch: () => void;
  update: (data: Partial<Evidence>) => void;
  hideEmptyFields: boolean;
  setHideEmptyFields: (hideEmptyFields: boolean) => void;
};

export type EvidenceContextProviderProps =
  | {
      children: React.ReactNode;
      evidence_id: number;
      uuid?: string;
    }
  | {
      children: React.ReactNode;
      uuid: string;
      evidence_id?: number;
    };

const EvidenceContext = createContext<EvidenceContextType | undefined>(
  undefined
);

export const EvidenceContextProvider: React.FC<EvidenceContextProviderProps> =
  ({ children, ...props }) => {
    const [hideEmptyFields, setHideEmptyFields] = useState(
      localStorage.getItem("evidence:details:hideEmptyFields") === "true"
    );

    const handleHideEmptyFieldsChange = (event: boolean) => {
      setHideEmptyFields(event);
      localStorage.setItem(
        "evidence:details:hideEmptyFields",
        event.toString()
      );
    };

    const { evidence_id, uuid } = props;

    const _query = {
      evidence_id,
      uuid,
    };

    const { data, isLoading, isFetched, refetch } = useQuery({
      queryKey: EvidenceQuery.queryKeys.list(_query),
      queryFn: () => EvidenceAPI.get(_query),
    });

    const {
      columnDefinitions,
      isLoading: isColumnDefinitionsLoading,
      isFetched: isColumnDefinitionsFetched,
    } = useEvidenceColumnDefinitions();

    const evidenceData = data?.data?.[0] || undefined;

    const evidenceUpdateMutation = EvidenceQuery.useMutateUpdateEvidence({
      query: _query,
    });

    const contextValue: EvidenceContextType = {
      data: evidenceData,
      isLoading,
      isFetched: isFetched,
      refetch,
      hideEmptyFields,
      setHideEmptyFields: handleHideEmptyFieldsChange,
      update: (data: Partial<Evidence>) => {
        if (!evidenceData?.evidence_id) {
          throw new Error("Evidence ID is required");
        }
        evidenceUpdateMutation.mutate({
          evidence_id: evidenceData?.evidence_id,
          ...data,
        });
      },
    };

    return (
      <EvidenceContext.Provider value={contextValue}>
        {children}
      </EvidenceContext.Provider>
    );
  };

export const useEvidenceContext = () => {
  const context = useContext(EvidenceContext);
  return context;
};
